import { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import BereitschaftsAuslastung, { AmpelPunkt } from '../components/BereitschaftsAuslastung';
import {
  bereitschaft,
  servers as serversApi,
  customers as customersApi,
  users as usersApi,
  type Bereitschaftsplan,
  type BereitschaftsStufe,
  type BereitschaftsMitglied,
  type BereitschaftsZeitregel,
  type Eskalationslauf,
  type EskalationsVorschau,
  type ZeitregelArt,
  type EskalationsEintrag,
} from '../api';
import AlarmePanel from '../components/AlarmePanel';

/**
 * Bereitschaft: wer wird geweckt, wenn etwas ausfällt.
 *
 * Zwei Hälften mit unterschiedlicher Dringlichkeit. Oben die **laufenden
 * Eskalationen** — was gerade brennt und wer sich kümmert. Unten die
 * **Pläne** — die Verabredung, wer wann dran ist.
 *
 * Die laufenden stehen oben, weil man diese Seite im Ernstfall öffnet, nicht
 * zum Pflegen. Wer nachts geweckt wird und hier landet, soll den
 * Übernehmen-Knopf sehen, ohne zu scrollen.
 */

const leereStufe = (ordnung: number): BereitschaftsStufe => ({
  ordnung,
  bezeichnung:
    ordnung === 1 ? 'Verantwortliche' : ordnung === 2 ? 'Vorgesetzte' : 'Geschäftsführung',
  nachMinuten: ordnung === 1 ? 0 : ordnung === 2 ? 180 : 420,
  mitglieder: [],
});

/**
 * Fertige Zeitregeln zum Anklicken.
 *
 * Die drei häufigen Fälle sollen ein Klick sein, nicht vier Eingaben. Wer
 * etwas anderes braucht, ändert die Werte danach — die Vorlage ist ein
 * Ausgangspunkt, keine Schablone.
 *
 * Faktor 3 bei Feiertagen und Wochenenden, 2 nachts: gegriffen, nicht
 * gemessen. An einem Feiertag wird aus einer Stunde bis zur nächsten Stufe
 * ein halber Tag — das ist die Größenordnung, in der niemand erwartet, dass
 * ein Kollege binnen einer Stunde am Rechner sitzt.
 */
const ZEITREGEL_VORLAGEN: Array<{ text: string; hinweis: string; regel: BereitschaftsZeitregel }> = [
  {
    text: 'Nachts nicht wecken',
    hinweis: 'Meldungen zwischen 20 und 8 Uhr warten bis zum Morgen',
    regel: {
      bezeichnung: 'Nachts nicht wecken',
      art: 'NACHT',
      vonStunde: 20,
      bisStunde: 8,
      aufschiebenBisStunde: 8,
      fristFaktor: 1,
      ausgesetzt: false,
      aktiv: true,
    },
  },
  {
    text: 'Wochenenden',
    hinweis: 'Fristen verdreifacht',
    regel: {
      bezeichnung: 'Wochenenden',
      art: 'WOCHENENDE',
      fristFaktor: 3,
      ausgesetzt: false,
      aktiv: true,
    },
  },
  {
    text: 'Weihnachten und Ostern',
    hinweis: 'nur diese Tage, Fristen verdreifacht',
    regel: {
      bezeichnung: 'Weihnachten und Ostern',
      art: 'FEIERTAG',
      bundesland: 'HE',
      feiertagsSchluessel: [
        'heiligabend', 'weihnachten1', 'weihnachten2', 'silvester', 'neujahr',
        'karfreitag', 'ostersonntag', 'ostermontag',
      ],
      fristFaktor: 3,
      ausgesetzt: false,
      aktiv: true,
    },
  },
  {
    text: 'Zeitraum …',
    hinweis: 'z. B. Betriebsferien',
    regel: {
      bezeichnung: 'Betriebsferien',
      art: 'ZEITRAUM',
      fristFaktor: 3,
      ausgesetzt: false,
      aktiv: true,
    },
  },
];

/** Die konkreten Feiertage zur Auswahl. Ohne Auswahl gelten alle. */
const FEIERTAGE_AUSWAHL: Array<[string, string]> = [
  ['neujahr', 'Neujahr'],
  ['karfreitag', 'Karfreitag'],
  ['ostersonntag', 'Ostersonntag'],
  ['ostermontag', 'Ostermontag'],
  ['maifeiertag', '1. Mai'],
  ['himmelfahrt', 'Christi Himmelfahrt'],
  ['pfingstmontag', 'Pfingstmontag'],
  ['fronleichnam', 'Fronleichnam (HE)'],
  ['einheit', '3. Oktober'],
  ['heiligabend', 'Heiligabend'],
  ['weihnachten1', '1. Weihnachtstag'],
  ['weihnachten2', '2. Weihnachtstag'],
  ['silvester', 'Silvester'],
];

/** Die 16 Länder — für die regionalen Feiertage. */
const BUNDESLAENDER = [
  ['BW', 'Baden-Württemberg'], ['BY', 'Bayern'], ['BE', 'Berlin'],
  ['BB', 'Brandenburg'], ['HB', 'Bremen'], ['HH', 'Hamburg'],
  ['HE', 'Hessen'], ['MV', 'Mecklenburg-Vorpommern'], ['NI', 'Niedersachsen'],
  ['NW', 'Nordrhein-Westfalen'], ['RP', 'Rheinland-Pfalz'], ['SL', 'Saarland'],
  ['SN', 'Sachsen'], ['ST', 'Sachsen-Anhalt'], ['SH', 'Schleswig-Holstein'],
  ['TH', 'Thüringen'],
] as const;

function minutenText(m: number): string {
  if (m === 0) return 'sofort';
  if (m < 60) return `${m} Min.`;
  const h = m / 60;
  return h % 1 === 0 ? `${h} Std.` : `${h.toFixed(1)} Std.`;
}

function dauerSeit(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 60) return `${min} Min.`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `${h} Std.` : `${h} Std. ${r} Min.`;
}

export default function BereitschaftPage() {
  const [plaene, setPlaene] = useState<Bereitschaftsplan[]>([]);
  const [laeufe, setLaeufe] = useState<Eskalationslauf[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeitet, setBearbeitet] = useState<Partial<Bereitschaftsplan> | null>(null);
  const [vorschau, setVorschau] = useState<EskalationsVorschau | null>(null);
  const [probeErgebnis, setProbeErgebnis] = useState<{
    titel: string;
    zeilen: string[];
  } | null>(null);
  const [uebernahmeFuer, setUebernahmeFuer] = useState<string | null>(null);
  const [notiz, setNotiz] = useState('');
  const [auswahl, setAuswahl] = useState<{
    server: Array<{ id: string; hostname: string }>;
    kunden: Array<{ id: string; name: string }>;
    nutzer: Array<{ id: string; name: string; email: string; slackId: string | null }>;
  }>({ server: [], kunden: [], nutzer: [] });

  const laden_ = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([bereitschaft.plaene(), bereitschaft.laeufe(true)]);
      setPlaene(p);
      setLaeufe(l);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    laden_();
    // Die laufenden Eskalationen veralten schnell — im Ernstfall schaut man
    // hier hin und will sehen, ob schon jemand übernommen hat.
    const t = setInterval(laden_, 30_000);
    return () => clearInterval(t);
  }, [laden_]);

  useEffect(() => {
    (async () => {
      try {
        const [s, k, n] = await Promise.all([
          serversApi.list().catch(() => []),
          customersApi.list().catch(() => []),
          usersApi.list().catch(() => []),
        ]);
        setAuswahl({
          server: (Array.isArray(s) ? s : []).map((x: any) => ({ id: x.id, hostname: x.hostname })),
          kunden: (Array.isArray(k) ? k : []).map((x: any) => ({ id: x.id, name: x.name })),
          nutzer: (Array.isArray(n) ? n : []).map((x: any) => ({
            id: x.id,
            name: x.name,
            email: x.email,
            slackId: x.slackId ?? null,
          })),
        });
      } catch {
        /* Auswahllisten sind Komfort, kein Muss. */
      }
    })();
  }, []);

  async function uebernehmen(alertId: string) {
    try {
      await bereitschaft.uebernehmen(alertId, notiz || undefined);
      setUebernahmeFuer(null);
      setNotiz('');
      await laden_();
    } catch (e: any) {
      setFehler(e?.response?.data?.error ?? 'Übernehmen fehlgeschlagen');
    }
  }

  async function speichern() {
    if (!bearbeitet) return;
    try {
      if (bearbeitet.id) await bereitschaft.aendern(bearbeitet.id, bearbeitet);
      else await bereitschaft.anlegen(bearbeitet);
      setBearbeitet(null);
      setVorschau(null);
      await laden_();
    } catch (e: any) {
      setFehler(e?.response?.data?.error ?? 'Speichern fehlgeschlagen');
    }
  }

  /**
   * Probelauf. Zwei Arten, zwei Fragen:
   * `zustellung` — kommt es an? `eskalation` — greift die Kette?
   *
   * Beide benachrichtigen wirklich, deshalb die Rückfrage: wer versehentlich
   * drauftippt, weckt sonst die halbe Belegschaft.
   */
  async function probelauf(id: string, modus: 'zustellung' | 'eskalation') {
    const frage =
      modus === 'eskalation'
        ? 'Übung starten? Es entsteht ein echter Alarm namens PROBELAUF, die Kette läuft ' +
          '60-fach beschleunigt durch und benachrichtigt dabei wirklich. Sie endet nach ' +
          '15 Minuten von selbst — oder sobald jemand "Ich übernehme" drückt.'
        : 'Probemeldung an alle Personen in diesem Plan schicken? Sie ist klar als Probe gekennzeichnet.';
    if (!confirm(frage)) return;

    try {
      const e = await bereitschaft.probelauf(id, modus);
      if (modus === 'eskalation') {
        setProbeErgebnis({
          titel: `Übung läuft — Plan "${e.plan}"`,
          zeilen: [
            `Die Kette läuft ${e.raffung}-fach beschleunigt: aus drei Stunden werden drei Minuten.`,
            'Oben unter "Laufende Eskalationen" ist sie zu sehen, samt Protokoll.',
            e.endetUm
              ? `Endet von selbst um ${new Date(e.endetUm).toLocaleTimeString('de-DE')}.`
              : '',
          ].filter(Boolean),
        });
      } else {
        setProbeErgebnis({
          titel: `Probemeldung verschickt — Plan "${e.plan}"`,
          zeilen: (e.zugestellt ?? []).map(
            (z) =>
              `Stufe ${z.stufe} · ${z.name}: Mail ${z.mail ? '✓' : '✗'} · ` +
              `Slack ${z.slack ? '✓' : '—'} · PC ${z.pc ? '✓' : '—'}` +
              (z.hinweis ? ` — ${z.hinweis}` : ''),
          ),
        });
      }
      await laden_();
    } catch (err) {
      const f = err as { response?: { data?: { error?: string } } };
      setFehler(f?.response?.data?.error ?? 'Probelauf fehlgeschlagen');
    }
  }

  async function vorschauHolen(id: string) {
    try {
      setVorschau(await bereitschaft.vorschau(id));
    } catch {
      setVorschau(null);
    }
  }

  if (laden) return <div className="p-4 text-sm text-slate-500">Wird geladen …</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Bereitschaft und Warnungen</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Was offen ist, wer geweckt wird — und was passiert, wenn niemand reagiert.
          Die Kette endet, sobald jemand „Ich übernehme" drückt.
        </p>
      </div>

      {fehler && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </div>
      )}

      {/* ══ VERTEILUNG DER LAST ════════════════════════════════════════════

          Steht vor den Plaenen, nicht dahinter: wer einen Plan aendert, soll
          vorher gesehen haben, wie es gerade verteilt ist — nicht hinterher. */}
      <BereitschaftsAuslastung />

      {/* ══ LAUFENDE ESKALATIONEN ══════════════════════════════════════════ */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          Laufende Eskalationen{' '}
          {laeufe.length > 0 && (
            <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-800">
              {laeufe.length}
            </span>
          )}
        </h2>

        {laeufe.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-white px-3 py-4 text-sm text-slate-500">
            Nichts offen. Hier stünde, wer gerade geweckt wird.
          </div>
        ) : (
          <div className="space-y-3">
            {laeufe.map((l) => (
              <div
                key={l.id}
                className={`rounded-md border bg-white p-3 shadow-sm ${
                  l.zustand === 'UEBERNOMMEN' ? 'border-teal-200' : 'border-red-200'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${
                          l.gemeldeteSchwere === 'critical' ? 'bg-red-600' : 'bg-amber-500'
                        }`}
                      />
                      <span className="truncate font-medium text-slate-800">
                        {l.alert.summary}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      Offen seit {dauerSeit(l.alert.startsAt)} · Stufe {l.aktuelleStufe} ·{' '}
                      {l.wiederholungen} Erinnerung(en) · Plan „{l.plan.name}"
                      {l.zeitregelName && (
                        <>
                          {' '}
                          · <span className="text-slate-600">{l.zeitregelName}</span> (Fristen ×
                          {l.fristFaktor})
                        </>
                      )}
                    </p>
                  </div>

                  <div className="shrink-0">
                    {l.zustand === 'UEBERNOMMEN' ? (
                      <div className="rounded bg-teal-50 px-2.5 py-1.5 text-xs text-teal-900">
                        <span className="font-semibold">
                          {l.uebernommenUser?.name ?? 'Jemand'}
                        </span>{' '}
                        hat um{' '}
                        {l.uebernommenAm
                          ? new Date(l.uebernommenAm).toLocaleTimeString('de-DE', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '?'}{' '}
                        Uhr übernommen
                      </div>
                    ) : uebernahmeFuer === l.alertId ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={notiz}
                          onChange={(e) => setNotiz(e.target.value)}
                          placeholder="Notiz (optional)"
                          className="w-48 rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <button
                          onClick={() => uebernehmen(l.alertId)}
                          className="rounded bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800"
                        >
                          Bestätigen
                        </button>
                        <button
                          onClick={() => {
                            setUebernahmeFuer(null);
                            setNotiz('');
                          }}
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setUebernahmeFuer(l.alertId)}
                        className="rounded bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800"
                      >
                        Ich übernehme
                      </button>
                    )}
                  </div>
                </div>

                <Vorfallsprotokoll lauf={l} onAenderung={laden_} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ══ OFFENE ALARME ═════════════════════════════════════════════════ */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">Alarme</h2>
        <AlarmePanel />
      </section>

      {/* ══ PLÄNE ═════════════════════════════════════════════════════════ */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Bereitschaftspläne</h2>
          <button
            onClick={() =>
              setBearbeitet({
                name: '',
                aktiv: true,
                istVorgabe: plaene.length === 0,
                giltFuerSchwere: ['critical'],
                wiederholungMinuten: 15,
                maxWiederholungen: 0,
                stufen: [leereStufe(1)],
                zeitregeln: [],
              })
            }
            className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-900"
          >
            Neuer Plan
          </button>
        </div>

        <div className="space-y-2">
          {plaene.length === 0 && !bearbeitet && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              Noch kein Plan angelegt. Ohne Plan bleibt es beim alten Verhalten: Alarme gehen
              an den Sammelkanal, niemand wird gezielt geweckt.
            </div>
          )}

          {plaene.map((p) => (
            <div key={p.id} className="rounded-md border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{p.name}</span>
                    {p.istVorgabe && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                        Vorgabe
                      </span>
                    )}
                    {!p.aktiv && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                        inaktiv
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {p.giltFuerSchwere.join(', ')}
                    {p.server && ` · nur ${p.server.hostname}`}
                    {p.customer && ` · nur ${p.customer.companyName}`}
                    {' · Erinnerung alle '}
                    {minutenText(p.wiederholungMinuten)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => vorschauHolen(p.id)}
                    className="text-xs text-slate-600 hover:text-slate-900"
                    title="Rechnet den Ablauf durch, ohne jemanden zu benachrichtigen."
                  >
                    Vorschau
                  </button>
                  <button
                    onClick={() => probelauf(p.id, 'zustellung')}
                    className="text-xs text-slate-600 hover:text-slate-900"
                    title="Schickt jedem im Plan eine Probemeldung — prüft, ob sie ankommt."
                  >
                    Probemeldung
                  </button>
                  <button
                    onClick={() => probelauf(p.id, 'eskalation')}
                    className="text-xs text-amber-700 hover:text-amber-900"
                    title="Lässt die echte Kette im Zeitraffer durchlaufen — prüft, ob die Eskalation greift."
                  >
                    Übung starten
                  </button>
                  <button
                    onClick={() => setBearbeitet(JSON.parse(JSON.stringify(p)))}
                    className="text-xs text-teal-700 hover:text-teal-900"
                  >
                    Bearbeiten
                  </button>
                </div>
              </div>

              <ol className="mt-2 space-y-1">
                {p.stufen.map((s) => (
                  <li key={s.ordnung} className="text-xs text-slate-600">
                    <span className="font-medium">
                      {s.ordnung}. {s.bezeichnung}
                    </span>{' '}
                    <span className="text-slate-400">ab {minutenText(s.nachMinuten)}</span>
                    {': '}
                    {s.mitglieder.length === 0 ? (
                      <span className="text-amber-700">niemand hinterlegt</span>
                    ) : (
                      s.mitglieder
                        .map((m) => `${m.name} (${m.prioritaet}.)`)
                        .join(', ')
                    )}
                  </li>
                ))}
              </ol>

              {p.zeitregeln.filter((z) => z.aktiv).length > 0 && (
                <p className="mt-1.5 text-xs text-slate-500">
                  Zeitregeln:{' '}
                  {p.zeitregeln
                    .filter((z) => z.aktiv)
                    .map((z) =>
                      z.ausgesetzt
                        ? `${z.bezeichnung} (ausgesetzt)`
                        : `${z.bezeichnung} (×${z.fristFaktor})`,
                    )
                    .join(', ')}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {probeErgebnis && (
        <section className="rounded-md border border-teal-300 bg-teal-50 p-3">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-teal-900">{probeErgebnis.titel}</h3>
            <button
              onClick={() => setProbeErgebnis(null)}
              className="text-xs text-teal-700 hover:text-teal-900"
            >
              Schließen
            </button>
          </div>
          <ul className="space-y-0.5">
            {probeErgebnis.zeilen.map((z, i) => (
              <li key={i} className="text-xs text-teal-900">
                {z}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ══ VORSCHAU ══════════════════════════════════════════════════════ */}
      {vorschau && (
        <section className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">
              Ablauf, wenn jetzt ein Alarm käme
            </h3>
            <button
              onClick={() => setVorschau(null)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Schließen
            </button>
          </div>
          {vorschau.ausgesetzt ? (
            <p className="text-sm text-amber-800">{vorschau.hinweis}</p>
          ) : (
            <>
              {vorschau.lage.regelName && (
                <p className="mb-2 text-xs text-slate-600">
                  Es gilt „{vorschau.lage.regelName}": Fristen ×{vorschau.lage.faktor}
                  {vorschau.lage.schwere !== 'critical' && `, gemeldet als ${vorschau.lage.schwere}`}
                </p>
              )}
              <ol className="space-y-1">
                {vorschau.schritte.map((s, i) => (
                  <li key={i} className="text-xs text-slate-700">
                    <span className="inline-block w-20 font-mono text-slate-500">
                      +{minutenText(s.minute)}
                    </span>
                    <span className="font-medium">{s.stufeName}</span> → {s.wer.join(', ')}
                  </li>
                ))}
              </ol>
              <p className="mt-2 text-xs text-slate-500">
                Danach Erinnerung alle {minutenText(vorschau.wiederholungAlle ?? 0)}, bis jemand
                übernimmt.
              </p>
            </>
          )}
        </section>
      )}

      {/* ══ BEARBEITEN ════════════════════════════════════════════════════ */}
      {bearbeitet && (
        <PlanFormular
          plan={bearbeitet}
          auswahl={auswahl}
          onChange={setBearbeitet}
          onSpeichern={speichern}
          onAbbrechen={() => setBearbeitet(null)}
          onLoeschen={
            bearbeitet.id
              ? async () => {
                  try {
                    await bereitschaft.loeschen(bearbeitet.id!);
                    setBearbeitet(null);
                    await laden_();
                  } catch (e: any) {
                    setFehler(e?.response?.data?.error ?? 'Löschen fehlgeschlagen');
                  }
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

// ══ FORMULAR ═══════════════════════════════════════════════════════════════

function PlanFormular({
  plan,
  auswahl,
  onChange,
  onSpeichern,
  onAbbrechen,
  onLoeschen,
}: {
  plan: Partial<Bereitschaftsplan>;
  auswahl: {
    server: Array<{ id: string; hostname: string }>;
    kunden: Array<{ id: string; name: string }>;
    nutzer: Array<{ id: string; name: string; email: string; slackId: string | null }>;
  };
  onChange: (p: Partial<Bereitschaftsplan>) => void;
  onSpeichern: () => void;
  onAbbrechen: () => void;
  onLoeschen?: () => void;
}) {
  // ══ WIE STARK TRAEGT WER SCHON? ═════════════════════════════════════════
  //
  // Wird neben jeder Person angezeigt, die man eintraegt. Sperrt nichts: der
  // PC ersetzt kein Gespraech mit dem Kollegen, er sorgt nur dafuer, dass man
  // die Lage kennt, bevor man ihn zum vierten Mal eintraegt.
  //
  // Der Hook steht ganz oben und vor jedem return -- ein Hook hinter einem
  // fruehen return gibt eine weisse Seite, die weder tsc noch der Build
  // bemerken.
  const auslastung = useQuery({
    queryKey: ['bereitschaft', 'auslastung', 90],
    queryFn: () => bereitschaft.auslastung(90),
    // Die Zahlen aendern sich nicht im Minutentakt, und das Formular wird oft
    // auf- und zugeklappt.
    staleTime: 5 * 60 * 1000,
  });
  const auslastungJeNutzer = new Map(
    (auslastung.data?.personen ?? [])
      .filter((p) => p.userId)
      .map((p) => [p.userId as string, p]),
  );

  const stufen = plan.stufen ?? [];
  const setStufen = (s: BereitschaftsStufe[]) => onChange({ ...plan, stufen: s });

  const feld = 'rounded border border-slate-300 px-2 py-1 text-xs';

  return (
    <section className="rounded-md border border-teal-300 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-800">
        {plan.id ? 'Plan bearbeiten' : 'Neuer Plan'}
      </h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-slate-600">
          Name
          <input
            value={plan.name ?? ''}
            onChange={(e) => onChange({ ...plan, name: e.target.value })}
            className={`mt-0.5 w-full ${feld}`}
            placeholder="z. B. Produktivbetrieb"
          />
        </label>

        <label className="text-xs text-slate-600">
          Erinnerung alle … Minuten
          <input
            type="number"
            min={1}
            value={plan.wiederholungMinuten ?? 15}
            onChange={(e) => onChange({ ...plan, wiederholungMinuten: Number(e.target.value) })}
            className={`mt-0.5 w-full ${feld}`}
          />
        </label>

        <label className="text-xs text-slate-600">
          Nur für Server (optional)
          <select
            value={plan.serverId ?? ''}
            onChange={(e) => onChange({ ...plan, serverId: e.target.value || null })}
            className={`mt-0.5 w-full ${feld}`}
          >
            <option value="">alle</option>
            {auswahl.server.map((s) => (
              <option key={s.id} value={s.id}>
                {s.hostname}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs text-slate-600">
          Nur für Kunde (optional)
          <select
            value={plan.customerId ?? ''}
            onChange={(e) => onChange({ ...plan, customerId: e.target.value || null })}
            className={`mt-0.5 w-full ${feld}`}
          >
            <option value="">alle</option>
            {auswahl.kunden.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
        {(['critical', 'warning', 'info'] as const).map((s) => (
          <label key={s} className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={(plan.giltFuerSchwere ?? []).includes(s)}
              onChange={(e) => {
                const alt = plan.giltFuerSchwere ?? [];
                onChange({
                  ...plan,
                  giltFuerSchwere: e.target.checked ? [...alt, s] : alt.filter((x) => x !== s),
                });
              }}
            />
            {s}
          </label>
        ))}
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={plan.istVorgabe ?? false}
            onChange={(e) => onChange({ ...plan, istVorgabe: e.target.checked })}
          />
          Vorgabe für alles ohne eigenen Plan
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={plan.aktiv ?? true}
            onChange={(e) => onChange({ ...plan, aktiv: e.target.checked })}
          />
          aktiv
        </label>
      </div>

      {/* ── Stufen ──────────────────────────────────────────────────────── */}
      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold text-slate-700">Eskalationsstufen</h4>
          <button
            onClick={() => setStufen([...stufen, leereStufe(stufen.length + 1)])}
            className="text-xs text-teal-700 hover:text-teal-900"
          >
            + Stufe
          </button>
        </div>

        <div className="space-y-3">
          {stufen.map((s, si) => (
            <div key={si} className="rounded border border-slate-200 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">{s.ordnung}.</span>
                <input
                  value={s.bezeichnung}
                  onChange={(e) => {
                    const k = [...stufen];
                    k[si] = { ...s, bezeichnung: e.target.value };
                    setStufen(k);
                  }}
                  className={`${feld} w-44`}
                />
                <label className="text-xs text-slate-600">
                  ab Minute
                  <input
                    type="number"
                    min={0}
                    value={s.nachMinuten}
                    onChange={(e) => {
                      const k = [...stufen];
                      k[si] = { ...s, nachMinuten: Number(e.target.value) };
                      setStufen(k);
                    }}
                    className={`ml-1 w-20 ${feld}`}
                  />
                </label>
                <span className="text-xs text-slate-400">({minutenText(s.nachMinuten)})</span>
                <button
                  onClick={() =>
                    setStufen(
                      stufen
                        .filter((_, i) => i !== si)
                        .map((x, i) => ({ ...x, ordnung: i + 1 })),
                    )
                  }
                  className="ml-auto text-xs text-red-600 hover:text-red-800"
                >
                  Stufe entfernen
                </button>
              </div>

              <div className="mt-2 space-y-1.5">
                {s.mitglieder.map((m, mi) => {
                  const nutzer = auswahl.nutzer.find((u) => u.id === m.userId);
                  // Wie stark diese Person ohnehin schon traegt. Sperrt nichts
                  // — es soll nur nicht unsichtbar sein, wen man da gerade zum
                  // vierten Mal eintraegt.
                  const last = m.userId ? auslastungJeNutzer.get(m.userId) : undefined;
                  const aendern = (neu: Partial<BereitschaftsMitglied>) => {
                    const k = [...stufen];
                    const mm = [...s.mitglieder];
                    mm[mi] = { ...m, ...neu };
                    k[si] = { ...s, mitglieder: mm };
                    setStufen(k);
                  };
                  return (
                    <div key={mi} className="flex flex-wrap items-center gap-1.5">
                      <select
                        value={m.userId ?? ''}
                        onChange={(e) => {
                          const u = auswahl.nutzer.find((x) => x.id === e.target.value);
                          aendern(
                            u
                              ? { userId: u.id, name: u.name, email: u.email, slackId: null }
                              : { userId: null },
                          );
                        }}
                        className={`${feld} w-40`}
                      >
                        <option value="">— Person ohne PC-Konto —</option>
                        {auswahl.nutzer.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>

                      {nutzer ? (
                        // Verknüpfter Nutzer: Mail und Slack-Kennung kommen aus
                        // seinem Konto. Hier editierbar zu machen hieße, eine
                        // zweite Wahrheit über dieselbe Person zu pflegen.
                        <span className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                          <span>{nutzer.email}</span>
                          {last && last.ampel !== 'unbestimmt' && last.ampel !== 'gruen' && (
                            <span
                              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${
                                last.ampel === 'rot'
                                  ? 'bg-rose-50 text-rose-800'
                                  : 'bg-amber-50 text-amber-800'
                              }`}
                              title={
                                `In den letzten 90 Tagen ${last.ansprachen} mal angesprochen, `
                                + `davon ${last.ausserhalb} abends/nachts und ${last.ruhetage} an `
                                + `Wochenenden oder Feiertagen; ${last.uebernahmen} mal uebernommen. `
                                + 'Kein Hindernis — nur ein Hinweis.'
                              }
                            >
                              <AmpelPunkt ampel={last.ampel} klein />
                              {last.ampel === 'rot' ? 'traegt viel' : 'ueber dem Mittel'}
                              {' · '}
                              {last.anteil.toFixed(1)}×
                            </span>
                          )}
                          {nutzer.slackId ? (
                            <span className="rounded bg-teal-50 px-1.5 py-0.5 font-mono text-teal-800">
                              {nutzer.slackId}
                            </span>
                          ) : (
                            <span
                              className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800"
                              title="Ohne Slack-ID bekommt die Person Mail und PC-Benachrichtigung, aber keine Erwähnung in Slack."
                            >
                              keine Slack-ID — unter Benutzer nachtragen
                            </span>
                          )}
                        </span>
                      ) : (
                        <>
                          <input
                            value={m.name}
                            onChange={(e) => aendern({ name: e.target.value })}
                            placeholder="Name"
                            className={`${feld} w-32`}
                          />
                          <input
                            value={m.email}
                            onChange={(e) => aendern({ email: e.target.value })}
                            placeholder="E-Mail"
                            className={`${feld} w-44`}
                          />
                          <input
                            value={m.slackId ?? ''}
                            onChange={(e) => aendern({ slackId: e.target.value || null })}
                            placeholder="Slack-ID"
                            className={`${feld} w-28 font-mono`}
                          />
                        </>
                      )}

                      <label className="text-xs text-slate-500">
                        Prio
                        <input
                          type="number"
                          min={1}
                          value={m.prioritaet}
                          onChange={(e) => aendern({ prioritaet: Number(e.target.value) })}
                          className={`ml-1 w-12 ${feld}`}
                        />
                      </label>
                      <label className="text-xs text-slate-500">
                        wartet
                        <input
                          type="number"
                          min={1}
                          value={m.wartezeitMinuten}
                          onChange={(e) => aendern({ wartezeitMinuten: Number(e.target.value) })}
                          className={`ml-1 w-14 ${feld}`}
                        />{' '}
                        Min.
                      </label>
                      <button
                        onClick={() => {
                          const k = [...stufen];
                          k[si] = { ...s, mitglieder: s.mitglieder.filter((_, i) => i !== mi) };
                          setStufen(k);
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
                <button
                  onClick={() => {
                    const k = [...stufen];
                    k[si] = {
                      ...s,
                      mitglieder: [
                        ...s.mitglieder,
                        {
                          name: '',
                          email: '',
                          slackId: null,
                          userId: null,
                          prioritaet: s.mitglieder.length + 1,
                          wartezeitMinuten: 10,
                          aktiv: true,
                        },
                      ],
                    };
                    setStufen(k);
                  }}
                  className="text-xs text-teal-700 hover:text-teal-900"
                >
                  + Person
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Zeitregeln ──────────────────────────────────────────────────── */}
      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between">
          <h4 className="text-xs font-semibold text-slate-700">Zeitregeln</h4>
          <span className="flex flex-wrap gap-2">
            {ZEITREGEL_VORLAGEN.map((v) => (
              <button
                key={v.text}
                onClick={() =>
                  onChange({
                    ...plan,
                    zeitregeln: [...(plan.zeitregeln ?? []), { ...v.regel }],
                  })
                }
                className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:border-teal-400 hover:text-teal-800"
                title={v.hinweis}
              >
                + {v.text}
              </button>
            ))}
          </span>
        </div>
        <p className="mb-2 text-xs text-slate-500">
          Strecken die Fristen, wenn ohnehin niemand am System arbeitet. Die Reihenfolge der
          Kette bleibt gleich — nur die Abstände wachsen. Treffen mehrere zu, gilt die mildeste.
        </p>

        <div className="space-y-1.5">
          {(plan.zeitregeln ?? []).map((z, zi) => {
            const setZ = (neu: Partial<typeof z>) => {
              const k = [...(plan.zeitregeln ?? [])];
              k[zi] = { ...z, ...neu };
              onChange({ ...plan, zeitregeln: k });
            };
            return (
              <div key={zi} className="flex flex-wrap items-center gap-1.5">
                <input
                  value={z.bezeichnung}
                  onChange={(e) => setZ({ bezeichnung: e.target.value })}
                  className={`${feld} w-36`}
                />
                <select
                  value={z.art}
                  onChange={(e) => setZ({ art: e.target.value as ZeitregelArt })}
                  className={`${feld} w-32`}
                >
                  <option value="NACHT">Nachts</option>
                  <option value="WOCHENENDE">Wochenende</option>
                  <option value="FEIERTAG">Feiertage</option>
                  <option value="ZEITRAUM">Zeitraum</option>
                </select>
                {z.art === 'NACHT' && (
                  <>
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={z.vonStunde ?? 22}
                      onChange={(e) => setZ({ vonStunde: Number(e.target.value) })}
                      className={`${feld} w-14`}
                    />
                    <span className="text-xs text-slate-400">bis</span>
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={z.bisStunde ?? 6}
                      onChange={(e) => setZ({ bisStunde: Number(e.target.value) })}
                      className={`${feld} w-14`}
                    />
                    <span className="text-xs text-slate-400">Uhr</span>
                  </>
                )}
                {z.art === 'FEIERTAG' && (
                  <select
                    value={z.bundesland ?? ''}
                    onChange={(e) => setZ({ bundesland: e.target.value || null })}
                    title="Für die regionalen Feiertage. Ohne Auswahl gelten nur die bundesweiten."
                    className={`${feld} w-44`}
                  >
                    <option value="">nur bundesweite</option>
                    {BUNDESLAENDER.map(([k, name]) => (
                      <option key={k} value={k}>
                        {name}
                      </option>
                    ))}
                  </select>
                )}
                {z.art === 'ZEITRAUM' && (
                  <>
                    <input
                      type="date"
                      value={z.vonDatum ? String(z.vonDatum).slice(0, 10) : ''}
                      onChange={(e) =>
                        setZ({ vonDatum: e.target.value ? `${e.target.value}T00:00:00` : null })
                      }
                      className={`${feld} w-36`}
                    />
                    <span className="text-xs text-slate-400">bis</span>
                    <input
                      type="date"
                      value={z.bisDatum ? String(z.bisDatum).slice(0, 10) : ''}
                      onChange={(e) =>
                        // Bis einschließlich: wer den 31.12. wählt, meint den
                        // ganzen Tag, nicht dessen ersten Sekundenbruchteil.
                        setZ({ bisDatum: e.target.value ? `${e.target.value}T23:59:59` : null })
                      }
                      className={`${feld} w-36`}
                    />
                  </>
                )}
                <label
                  className="text-xs text-slate-500"
                  title="Streckt die Abstände in der Kette. 3 macht aus einer Stunde drei."
                >
                  Fristen ×
                  <input
                    type="number"
                    step={0.5}
                    min={0.1}
                    value={z.fristFaktor}
                    onChange={(e) => setZ({ fristFaktor: Number(e.target.value) })}
                    className={`ml-1 w-16 ${feld}`}
                  />
                </label>
                <label
                  className="flex items-center gap-1 text-xs text-slate-500"
                  title="Statt wecken: die Meldung wartet bis zu dieser Uhrzeit. Der Alarm bleibt offen und sichtbar — er weckt nur niemanden."
                >
                  <input
                    type="checkbox"
                    checked={z.aufschiebenBisStunde !== null && z.aufschiebenBisStunde !== undefined}
                    onChange={(e) => setZ({ aufschiebenBisStunde: e.target.checked ? 8 : null })}
                  />
                  erst ab
                  {z.aufschiebenBisStunde !== null && z.aufschiebenBisStunde !== undefined && (
                    <>
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={z.aufschiebenBisStunde}
                        onChange={(e) => setZ({ aufschiebenBisStunde: Number(e.target.value) })}
                        className={`w-12 ${feld}`}
                      />
                      Uhr
                    </>
                  )}
                </label>
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={z.ausgesetzt}
                    onChange={(e) => setZ({ ausgesetzt: e.target.checked })}
                    title="Gar nicht benachrichtigen. Mit Bedacht — was hier stumm bleibt, fällt erst am nächsten Werktag auf."
                  />
                  ganz aussetzen
                </label>
                <button
                  onClick={() =>
                    onChange({
                      ...plan,
                      zeitregeln: (plan.zeitregeln ?? []).filter((_, i) => i !== zi),
                    })
                  }
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  ✕
                </button>

                {z.art === 'FEIERTAG' && (
                  <div className="w-full pl-2">
                    <p className="mb-1 text-xs text-slate-400">
                      Welche Tage? Ohne Auswahl gelten alle gesetzlichen.
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {FEIERTAGE_AUSWAHL.map(([k, name]) => {
                        const gewaehlt = z.feiertagsSchluessel ?? [];
                        const an = gewaehlt.includes(k);
                        return (
                          <label key={k} className="flex items-center gap-1 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={an}
                              onChange={(e) =>
                                setZ({
                                  feiertagsSchluessel: e.target.checked
                                    ? [...gewaehlt, k]
                                    : gewaehlt.filter((x) => x !== k),
                                })
                              }
                            />
                            {name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={onSpeichern}
          className="rounded bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800"
        >
          Speichern
        </button>
        <button onClick={onAbbrechen} className="text-xs text-slate-600 hover:text-slate-900">
          Abbrechen
        </button>
        {onLoeschen && (
          <button onClick={onLoeschen} className="ml-auto text-xs text-red-600 hover:text-red-800">
            Plan löschen
          </button>
        )}
      </div>
    </section>
  );
}

// ══ VORFALLSPROTOKOLL ══════════════════════════════════════════════════════

const ART_SYMBOL: Record<EskalationsEintrag['art'], string> = {
  START: '▶',
  ESKALATION: '↑',
  ZUSTELLUNG: '✉',
  UEBERNAHME: '✋',
  NOTIZ: '✎',
  WEITERGABE: '↪',
  ABSCHLUSS: '✓',
};

const ART_FARBE: Record<EskalationsEintrag['art'], string> = {
  START: 'text-slate-400',
  ESKALATION: 'text-amber-600',
  ZUSTELLUNG: 'text-slate-400',
  UEBERNAHME: 'text-teal-700',
  NOTIZ: 'text-slate-700',
  WEITERGABE: 'text-amber-700',
  ABSCHLUSS: 'text-teal-700',
};

/**
 * Was ist passiert, und was hat wer versucht.
 *
 * Systemzeilen und Handnotizen stehen in einer Liste, weil man sie zusammen
 * liest: „21:02 übernommen, 21:14 Datenbank neu gestartet, 21:30 half nicht,
 * abgegeben" ist eine Geschichte. Getrennt wären es zwei Listen, die niemand
 * übereinanderlegt.
 *
 * Die Notiz ist absichtlich prominent und nicht hinter einem Klick versteckt:
 * wer nachts um vier übergibt, schreibt nur auf, was ohne Umweg erreichbar ist.
 */
function Vorfallsprotokoll({
  lauf,
  onAenderung,
}: {
  lauf: Eskalationslauf;
  onAenderung: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const [eintraege, setEintraege] = useState<EskalationsEintrag[]>([]);
  const [text, setText] = useState('');
  const [modus, setModus] = useState<'notiz' | 'weitergabe'>('notiz');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  const holen = useCallback(async () => {
    setLaedt(true);
    try {
      setEintraege(await bereitschaft.protokoll(lauf.alertId));
    } catch {
      /* Ein leeres Protokoll ist kein Fehler, den man anschreien müsste. */
    } finally {
      setLaedt(false);
    }
  }, [lauf.alertId]);

  useEffect(() => {
    if (offen) holen();
  }, [offen, holen]);

  async function senden() {
    const t = text.trim();
    if (!t) return;
    try {
      if (modus === 'weitergabe') await bereitschaft.weitergeben(lauf.alertId, t);
      else await bereitschaft.notiz(lauf.alertId, t);
      setText('');
      setFehler(null);
      await holen();
      onAenderung();
    } catch (e: any) {
      setFehler(e?.response?.data?.error ?? 'Konnte nicht gespeichert werden');
    }
  }

  return (
    <div className="mt-2 border-t border-slate-100 pt-2">
      <button
        onClick={() => setOffen((o) => !o)}
        className="text-xs text-slate-500 hover:text-slate-700"
      >
        {offen ? '▾' : '▸'} Vorfallsprotokoll
        {eintraege.length > 0 && ` (${eintraege.length})`}
      </button>

      {offen && (
        <div className="mt-2">
          {laedt && <p className="text-xs text-slate-400">Wird geladen …</p>}

          <ol className="space-y-1">
            {eintraege.map((e) => (
              <li key={e.id} className="flex gap-2 text-xs">
                <span className="w-11 shrink-0 font-mono text-slate-400">
                  {new Date(e.createdAt).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className={`shrink-0 ${ART_FARBE[e.art]}`}>{ART_SYMBOL[e.art]}</span>
                <span className={e.art === 'NOTIZ' || e.art === 'WEITERGABE' ? 'text-slate-800' : 'text-slate-500'}>
                  {(e.art === 'NOTIZ' || e.art === 'WEITERGABE') && (e.autorName || e.user?.name) && (
                    <span className="font-medium">{e.autorName ?? e.user?.name}: </span>
                  )}
                  {e.text}
                </span>
              </li>
            ))}
            {!laedt && eintraege.length === 0 && (
              <li className="text-xs text-slate-400">Noch keine Einträge.</li>
            )}
          </ol>

          <div className="mt-2 space-y-1.5">
            <div className="flex gap-3 text-xs">
              <label className="flex items-center gap-1 text-slate-600">
                <input
                  type="radio"
                  checked={modus === 'notiz'}
                  onChange={() => setModus('notiz')}
                />
                Notiz
              </label>
              <label
                className="flex items-center gap-1 text-slate-600"
                title="Gibt die Übernahme zurück — die Kette läuft weiter, statt stumm zu bleiben."
              >
                <input
                  type="radio"
                  checked={modus === 'weitergabe'}
                  onChange={() => setModus('weitergabe')}
                />
                Ich komme nicht weiter
              </label>
            </div>

            <div className="flex gap-1.5">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') senden();
                }}
                placeholder={
                  modus === 'weitergabe'
                    ? 'Was wurde versucht, was steht noch aus?'
                    : 'Was hast du getan oder herausgefunden?'
                }
                className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
              />
              <button
                onClick={senden}
                className={`rounded px-2.5 py-1 text-xs font-semibold text-white ${
                  modus === 'weitergabe'
                    ? 'bg-amber-700 hover:bg-amber-800'
                    : 'bg-slate-700 hover:bg-slate-800'
                }`}
              >
                {modus === 'weitergabe' ? 'Abgeben' : 'Eintragen'}
              </button>
            </div>

            {modus === 'weitergabe' && (
              <p className="text-xs text-amber-700">
                Die Eskalation läuft danach weiter — bei der nächsthöheren Stufe, falls es eine
                gibt.
              </p>
            )}
            {fehler && <p className="text-xs text-red-700">{fehler}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
